import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import CookieConsent from "react-cookie-consent";

import HomePage from './pages/HomePage';
import Header from './components/Header';
import Footer from './components/Footer';
import PrivacyPage from './pages/PrivacyPage';
import CharactersPage from './pages/CharactersPage';
import CharacterPage from './pages/CharacterPage';
import SkillsPage from './pages/SkillsPage';
import BuffsPage from './pages/BuffsPage';

import { fetchGenericData, fetchVersionData, saveData } from './utils/firebaseUtils';
import { checkVersionData } from './utils/versionDataUtil';

import { processAllSkills } from './components/Skill';
import AutoScroll from './components/AutoScroll';


const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => { },
});

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);


  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const value = { theme, toggleTheme };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  return useContext(ThemeContext);
};

export { ThemeProvider, useTheme, ThemeContext };

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AutoScroll />
        <Routes>
          <Route path="*" element={<Main />} />
        </Routes>
      </Router>
    </ThemeProvider>

  );

}

function Main() {
  const location = useLocation();
  const { theme } = useTheme();
  const [versionData, setVersionData] = useState();
  const [characterData, setCharacterData] = useState({});
  const [buffData, setBuffData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(false);
  const [errorKey, setErrorKey] = useState("U");

  useEffect(() => {
    async function fetchData() {
      try {
        const fetchedVersionData = await checkVersionData(versionData, fetchVersionData);
        if (!fetchedVersionData) {
          setErrorKey("V");
          setErrors(true);
        }
        setVersionData(fetchedVersionData);

        let fetchedBuffData = await fetchGenericData(fetchedVersionData, setVersionData, 'buffs');
        if (!fetchedBuffData) {
          setErrorKey("B");
          setErrors(true);
        }
        setBuffData(fetchedBuffData.data);

        let fetchedCharacterData = await fetchGenericData(fetchedVersionData, setVersionData, 'characters');
        if (!fetchedCharacterData) {
          setErrorKey("C");
          setErrors(true);
        }
        let characters = fetchedCharacterData.data;

        if (fetchedCharacterData && fetchedCharacterData.versionUpdated) {
          characters = processAllSkills(characters, fetchedBuffData.data);
          await saveData(characters, 'characters', 'data');
        }
        setCharacterData(characters);


      } catch (error) {
        console.error('Error fetching data:', error);

        setErrors(true);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || errors) {
    return (
      <div className={`App ${theme}`}>
        <Header />
        <div className="page">
          {errors && (
            <FormattedMessage
              id="home.error"
              defaultMessage="An error occurred while retrieving data. Please refresh the page or check your browser's cache settings [{e}]."
              values={{ e: errorKey }}
            />
          )}
        </div>
        {location.pathname !== '/privacy' && <Footer />}
      </div>
    );
  }

  return (
    <div className={`App ${theme}`}>
      <CookieConsent
        location="bottom"
        buttonText={<FormattedMessage
          id="cookie.accept"
          defaultMessage="Accept"
        />}
        style={{ color: theme === 'dark' ? 'rgb(186, 186, 186)' : 'black', background: theme === 'dark' ? 'rgb(37, 37, 37)' : '#CFD8DC' }}
        buttonStyle={{ background: theme === 'dark' ? 'rgb(206, 206, 206)' : '#EDE7F6', borderRadius: "2px" }}
        expires={150}
      >
        <FormattedMessage
          id="cookie.message"
          defaultMessage="This website uses cookies to enhance the user experience."
        />
      </CookieConsent>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/characters" element={<CharactersPage characterData={characterData} />} />
        <Route path="/skills" element={<SkillsPage characterData={characterData} buffData={buffData} />} />
        <Route path="/buffs" element={<BuffsPage buffData={buffData} />} />
        {characterData && Object.values(characterData).map((character) => (
          <Route
            key={character.id}
            path={`/character/${character.id}`}
            element={<CharacterPage character={character} buffData={buffData} />}
          />
        ))}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {location.pathname !== '/privacy' && <Footer />}
    </div>
  );
}

export default App;